import React, { useEffect, useRef, useState } from 'react'
import { CityPlace, CitySuggestion } from '@ecountabl/api-client'
import api from './api-client'

const initialState = {
  visible: false,
  query: '',
  suggestions: [] as CitySuggestion[],
  place: null as CityPlace | null,
  error: '',
}

const errorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'City search failed'
}

export const TestCityTypeahead = (): React.ReactElement => {
  const [state, setState] = useState(initialState)
  const requestId = useRef(0)

  useEffect(() => {
    if (!state.visible) return undefined
    const trimmed = state.query.trim()
    if (state.place && trimmed === state.place.label) return undefined
    if (trimmed.length < 2) {
      setState((current) => (
        current.suggestions.length ? { ...current, suggestions: [] } : current
      ))
      return undefined
    }

    const timeout = window.setTimeout(() => {
      const currentRequest = requestId.current + 1
      requestId.current = currentRequest
      api.searchCities(trimmed).then((response) => {
        if (requestId.current !== currentRequest) return
        const cities = Array.isArray(response.cities) ? response.cities : []
        setState((current) => ({
          ...current,
          suggestions: cities,
          error: cities.length ? '' : 'No matching cities',
        }))
      }).catch((searchError) => {
        if (requestId.current !== currentRequest) return
        setState((current) => ({
          ...current,
          suggestions: [],
          error: errorMessage(searchError),
        }))
      })
    }, 300)

    return () => window.clearTimeout(timeout)
  }, [state.query, state.visible, state.place])

  const closeModal = (): void => {
    requestId.current += 1
    setState(initialState)
  }

  const selectCity = (suggestion: CitySuggestion): void => {
    const currentRequest = requestId.current + 1
    requestId.current = currentRequest
    setState((current) => ({ ...current, suggestions: [], error: '' }))
    api.getCityPlace(suggestion.placeId).then((place) => {
      if (requestId.current !== currentRequest) return
      setState((current) => ({
        ...current,
        query: place.label,
        place,
        suggestions: [],
        error: '',
      }))
    }).catch((detailsError) => {
      if (requestId.current !== currentRequest) return
      setState((current) => ({
        ...current,
        place: null,
        error: errorMessage(detailsError),
      }))
    })
  }

  return <>
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => setState({ ...initialState, visible: true })}
    >
      Test City Typeahead
    </button>
    <div
      className="central-modal"
      style={{ display: state.visible ? 'flex' : 'none' }}
      onPointerDown={closeModal}
    >
      <div
        className="modal-content"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <h1>City typeahead</h1>
        <p>Type a city. Choosing a suggestion resolves the canonical city, state, and coordinates.</p>
        <div className="form-group">
          <label htmlFor="city-typeahead-query">City</label>
          <input
            id="city-typeahead-query"
            type="text"
            value={state.query}
            autoComplete="off"
            placeholder="Mt Dora"
            onChange={(event) => {
              const query = event.target.value
              setState((current) => ({
                ...current,
                query,
                place: current.place && query === current.place.label ? current.place : null,
                error: '',
              }))
            }}
          />
          {state.suggestions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35em', marginTop: '0.5em' }}>
              {state.suggestions.map((suggestion) => (
                <button
                  key={suggestion.placeId}
                  type="button"
                  className="btn btn-secondary"
                  style={{ textAlign: 'left' }}
                  onClick={() => selectCity(suggestion)}
                >
                  {suggestion.label}
                </button>
              ))}
            </div>
          )}
          {state.place && (
            <p>
              {state.place.city}, {state.place.state}
              {' · '}
              {state.place.latitude.toFixed(4)}, {state.place.longitude.toFixed(4)}
            </p>
          )}
          {!!state.error && <p style={{ color: '#b91c1c' }}>{state.error}</p>}
        </div>
        <div style={{ marginTop: '1.5em' }}>
          <button type="button" className="btn btn-secondary" onClick={closeModal}>Close</button>
        </div>
      </div>
    </div>
  </>
}

export default TestCityTypeahead
